type OffsetPageResponse<R> = {
  total: number;
  items: Array<R>;
  limit?: number | null;
  offset?: number | null;
};

type HCCPageResponse<R> = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<R>;
};

// Generic pagination helper
const fetchAllPagesGeneric = async <T extends { limit: number; offset: number }, B, R>(
  fetchPage: (args: T) => Promise<B>,
  convert: (result: B) => { items: R[]; total: number },
  pageSize: number
) => {
  const allItems: R[] = [];

  // Get first page
  const firstResponse = convert(await fetchPage({ offset: 0, limit: pageSize } as T));
  allItems.push(...firstResponse.items);

  // Calculate remaining pages and fetch them in parallel
  // Use actual page size from response, not the requested size, to account for endpoints
  // that ignore the limit parameter
  const actualPageSize = firstResponse.items.length;
  const remainingPages = Math.ceil(
    (firstResponse.total - firstResponse.items.length) / actualPageSize
  );

  if (remainingPages > 0) {
    const promises = [...new Array(remainingPages)].map((_, index) =>
      fetchPage({ offset: (index + 1) * actualPageSize, limit: pageSize } as T).then(convert)
    );
    const otherItems = (await Promise.all(promises)).flatMap(({ items }) => items);
    allItems.push(...otherItems);
  }

  return { items: allItems };
};

// Used to fetch all items from an offset-based paginated endpoint
export const fetchAllPages = async <T extends { limit: number; offset: number }, R>(
  fetchPage: (params: T) => Promise<OffsetPageResponse<R>>
) => {
  return fetchAllPagesGeneric(fetchPage, (res) => res, 100);
};

// Used to fetch all items from a URL-based paginated endpoint (e.g., Django REST Framework style)
export const fetchAllPagesHcc = async <T extends { limit: number; offset: number }, R>(
  fetchPage: (params: T) => Promise<HCCPageResponse<R>>
) => {
  const items = await fetchAllPagesGeneric(
    fetchPage,
    (res) => ({ items: res.results, total: res.count }),
    10_000 // HCC API does not fail when you request a large limit but will just send back the amount of items it can handle, so we can just request a large limit and let the API decide how many items to return per page
  );

  return { count: items.items.length, results: items.items };
};
